import type { TrialRecap } from '@/hooks/useTrialRecap';
import { Button } from '@/components/ui/button';
import { Target, Flame } from 'lucide-react';

interface Props {
  recap: TrialRecap | null;
  onContinue: () => void;
}

function formatMoney(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(0)}`;
  }
}

export function PageGoalsSavings({ recap, onContinue }: Props) {
  const saved = recap?.goals.goalsSaved ?? 0;
  const created = recap?.goals.goalsCreated ?? 0;
  const streak = recap?.goals.currentStreak ?? 0;
  const milestones = recap?.goals.milestonesHit ?? 0;
  const currency = recap?.currency ?? 'USD';

  return (
    <div className="min-h-full flex flex-col px-6 py-12 max-w-lg mx-auto">
      <p className="text-[10px] uppercase tracking-[0.3em] text-primary/70 mb-3">
        Chapter three
      </p>
      <h1 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight mb-6">
        I put money<br />toward what matters.
      </h1>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="rounded-2xl border border-border/30 bg-muted/5 p-5">
          <div className="flex items-center gap-2 mb-2 text-primary/80">
            <Target className="h-4 w-4" />
            <span className="text-[10px] uppercase tracking-widest">
              Saved
            </span>
          </div>
          <p className="text-2xl font-bold tracking-tight">
            {formatMoney(saved, currency)}
          </p>
          <p className="text-[11px] text-muted-foreground/60 mt-1">
            across {created} goal{created === 1 ? '' : 's'}
          </p>
        </div>

        <div className="rounded-2xl border border-border/30 bg-muted/5 p-5">
          <div className="flex items-center gap-2 mb-2 text-primary/80">
            <Flame className="h-4 w-4" />
            <span className="text-[10px] uppercase tracking-widest">
              Streak
            </span>
          </div>
          <p className="text-2xl font-bold tracking-tight">
            {streak}
            <span className="text-sm font-normal text-muted-foreground/60 ms-1">
              days
            </span>
          </p>
          <p className="text-[11px] text-muted-foreground/60 mt-1">
            of discipline
          </p>
        </div>
      </div>

      {milestones > 0 && (
        <div className="rounded-2xl border border-border/30 bg-muted/5 p-5 mb-6">
          <p className="text-sm text-muted-foreground/80">
            <span className="font-semibold text-foreground">{milestones}</span>{' '}
            goal{milestones === 1 ? '' : 's'} passed the halfway mark.
          </p>
        </div>
      )}

      <p className="text-xs text-muted-foreground/60 leading-relaxed mb-8">
        Free keeps 1 goal. Any others pause until I continue.
      </p>

      <div className="mt-auto pt-6">
        <Button onClick={onContinue} className="w-full" size="lg">
          Continue
        </Button>
      </div>
    </div>
  );
}
